import { useMemo, useState } from 'react'
import { Alert, Button, Popconfirm, Select, Space, Spin, Switch, Table, Tag, message } from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'
import { draftUsersService, type DraftAllowlistUser, type IamDirectoryUser } from '@/services'
import { useAuthStore } from '@/stores/authStore'
import { PageContainer } from '@/components/common/PageContainer'
import { PageHeader } from '@/components/common/PageHeader'
import { SectionCard } from '@/components/common/SectionCard'
import { displayUserName, formatDateTime } from '@/utils/cn'

/**
 * Allowlist key used for draft CUSTOUT permission checks (matches mobile UserId).
 * Prefer IAM employee_code; fall back to email/username when code is missing.
 */
function resolveAllowlistKey(user: IamDirectoryUser): string | null {
  const code = user.employee_code?.trim()
  if (code) return code
  const email = user.email?.trim()
  if (email) return email
  const username = user.username?.trim()
  if (username) return username
  return null
}

export default function DraftUsersPage() {
  const queryClient = useQueryClient()
  const actor = displayUserName(useAuthStore((s) => s.user)) || undefined
  const [selectedEmployeeCode, setSelectedEmployeeCode] = useState<string | undefined>()

  const allowlistQ = useQuery({
    queryKey: ['custout-draft-users'],
    queryFn: () => draftUsersService.listAllowlist(),
  })

  const iamQ = useQuery({
    queryKey: ['iam-users-directory'],
    queryFn: () => draftUsersService.listIamUsers(),
  })

  const allowlistCodes = useMemo(() => {
    const set = new Set<string>()
    for (const row of allowlistQ.data ?? []) {
      if (row.employeeCode) set.add(row.employeeCode.trim().toUpperCase())
    }
    return set
  }, [allowlistQ.data])

  const iamOptions = useMemo(() => {
    const seen = new Set<string>()
    const options: Array<{
      value: string
      label: string
      disabled: boolean
      user: IamDirectoryUser
    }> = []

    for (const u of iamQ.data ?? []) {
      const key = resolveAllowlistKey(u)
      if (!key) continue
      const keyUpper = key.toUpperCase()
      if (seen.has(keyUpper)) continue
      seen.add(keyUpper)

      const already = allowlistCodes.has(keyUpper)
      const label = [u.name, key, u.email && u.email !== key ? u.email : null]
        .filter(Boolean)
        .join(' · ')
      options.push({
        value: key,
        label: already ? `${label} (already added)` : label,
        disabled: already,
        user: u,
      })
    }
    return options
  }, [iamQ.data, allowlistCodes])

  const selectedIamUser: IamDirectoryUser | undefined = useMemo(() => {
    if (!selectedEmployeeCode) return undefined
    const selected = selectedEmployeeCode.trim().toUpperCase()
    return (iamQ.data ?? []).find((u) => resolveAllowlistKey(u)?.toUpperCase() === selected)
  }, [iamQ.data, selectedEmployeeCode])

  const addMutation = useMutation({
    mutationFn: async () => {
      if (!selectedEmployeeCode?.trim()) throw new Error('Select a user from IAM')
      const code = selectedEmployeeCode.trim()
      const existing = (allowlistQ.data ?? []).find(
        (r) => r.employeeCode.trim().toUpperCase() === code.toUpperCase()
      )
      if (existing) {
        if (!existing.isActive) {
          return draftUsersService.updateAllowlistUser(existing.id, {
            is_active: true,
            display_name: selectedIamUser?.name ?? existing.displayName ?? undefined,
            actor,
          })
        }
        throw new Error('User is already on the draft allowlist')
      }
      return draftUsersService.addToAllowlist({
        employee_code: code,
        display_name: selectedIamUser?.name ?? undefined,
        actor,
      })
    },
    onSuccess: () => {
      message.success('Added to draft allowlist')
      setSelectedEmployeeCode(undefined)
      void queryClient.invalidateQueries({ queryKey: ['custout-draft-users'] })
    },
    onError: (err: unknown) => {
      const axiosMsg =
        (err as { response?: { data?: { error?: string } }; message?: string })?.response?.data
          ?.error || (err as { message?: string })?.message
      message.error(axiosMsg || 'Failed to add user')
    },
  })

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      draftUsersService.updateAllowlistUser(id, { is_active: isActive, actor }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['custout-draft-users'] })
    },
    onError: () => message.error('Failed to update user'),
  })

  const removeMutation = useMutation({
    mutationFn: (id: number) => draftUsersService.removeFromAllowlist(id),
    onSuccess: () => {
      message.success('Removed from draft allowlist')
      void queryClient.invalidateQueries({ queryKey: ['custout-draft-users'] })
    },
    onError: () => message.error('Failed to remove user'),
  })

  const loading = allowlistQ.isLoading || iamQ.isLoading

  if (loading) {
    return (
      <PageContainer fluid>
        <div className="flex justify-center py-20">
          <Spin size="large" />
        </div>
      </PageContainer>
    )
  }

  return (
    <PageContainer fluid>
      <PageHeader
        title="Draft CUSTOUT users"
        subtitle="Allowlist who can save field CUSTOUT as draft (In Transit). Users are chosen from IAM (read-only); changes write only to the local RFID allowlist."
      />

      {iamQ.isError ? (
        <Alert
          className="mb-4"
          type="warning"
          showIcon
          message="Could not load IAM user directory"
          description={(iamQ.error as { message?: string })?.message}
        />
      ) : null}

      {allowlistQ.isError ? (
        <Alert
          className="mb-4"
          type="error"
          showIcon
          message="Could not load draft allowlist"
          description={(allowlistQ.error as { message?: string })?.message}
        />
      ) : null}

      <SectionCard title="Add from IAM" className="mb-5">
        <Space wrap className="w-full" size="middle">
          <Select
            showSearch
            allowClear
            placeholder="Search IAM users by name, code, or email"
            className="min-w-[320px] w-[420px] max-w-full"
            value={selectedEmployeeCode}
            onChange={(v) => setSelectedEmployeeCode(v)}
            optionFilterProp="label"
            options={iamOptions}
            disabled={iamQ.isError}
          />
          <Button
            type="primary"
            icon={<Plus className="h-4 w-4" />}
            loading={addMutation.isPending}
            disabled={!selectedEmployeeCode}
            onClick={() => addMutation.mutate()}
          >
            Add to allowlist
          </Button>
        </Space>
      </SectionCard>

      <SectionCard title="Allowlist" bodyClassName="!p-0">
        <Table<DraftAllowlistUser>
          size="small"
          rowKey="id"
          pagination={{ pageSize: 20, showSizeChanger: true }}
          dataSource={allowlistQ.data ?? []}
          columns={[
            {
              title: 'Employee code',
              dataIndex: 'employeeCode',
              sorter: (a, b) => a.employeeCode.localeCompare(b.employeeCode),
            },
            {
              title: 'Display name',
              dataIndex: 'displayName',
              render: (v: string | null) => v || '—',
            },
            {
              title: 'Plant',
              dataIndex: 'plantCode',
              width: 100,
              render: (v: string | null) => v || '—',
            },
            {
              title: 'Status',
              dataIndex: 'isActive',
              width: 120,
              render: (active: boolean, row) => (
                <Space>
                  <Switch
                    size="small"
                    checked={active}
                    loading={toggleMutation.isPending && toggleMutation.variables?.id === row.id}
                    onChange={(checked) =>
                      toggleMutation.mutate({ id: row.id, isActive: checked })
                    }
                  />
                  <Tag color={active ? 'green' : 'default'}>{active ? 'Active' : 'Inactive'}</Tag>
                </Space>
              ),
            },
            {
              title: 'Updated',
              dataIndex: 'updatedAt',
              width: 170,
              render: (v: string | null | undefined) => (v ? formatDateTime(v) : '—'),
            },
            {
              title: '',
              key: 'actions',
              width: 90,
              render: (_: unknown, row) => (
                <Popconfirm
                  title="Remove from allowlist?"
                  description="This only removes the local draft allowlist entry. IAM is not changed."
                  okText="Remove"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => removeMutation.mutate(row.id)}
                >
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<Trash2 className="h-4 w-4" />}
                    loading={removeMutation.isPending && removeMutation.variables === row.id}
                  />
                </Popconfirm>
              ),
            },
          ]}
        />
      </SectionCard>
    </PageContainer>
  )
}
